import { useState, useEffect, useCallback, useMemo } from 'react';
import type { TreeProps } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { getAncestors } from 'components/common/popover-tree-selector/tree-utils';

export enum ExpansionMode {
  EXPAND_ROOT_NODES = 'EXPAND_ROOT_NODES'
}

interface UsePopoverTreeExpansionParams {
  isPopoverOpen: boolean;
  selectedKey?: React.Key;
  searchMatchKeys: React.Key[];
  parentMap: Record<React.Key, React.Key | null>;
  expansionMode?: ExpansionMode;
}

export const usePopoverTreeExpansion = ({
  isPopoverOpen,
  selectedKey,
  searchMatchKeys,
  parentMap,
  expansionMode
}: UsePopoverTreeExpansionParams) => {
  const [manuallyExpandedKeys, setManuallyExpandedKeys] = useState<React.Key[]>([]);

  const calculatedExpandedKeys = useMemo(() => {
    if (!isPopoverOpen) {
      return [];
    }

    const expandedByMode =
      expansionMode === ExpansionMode.EXPAND_ROOT_NODES
        ? Object.keys(parentMap).filter(key => parentMap[key] === null)
        : [];

    if (!isEmpty(searchMatchKeys)) {
      const searchMatchAncestors = getAncestors(searchMatchKeys, parentMap);
      return Array.from(new Set<React.Key>([...searchMatchKeys, ...searchMatchAncestors, ...expandedByMode]));
    } else if (selectedKey) {
      const selectedKeyAncestors = getAncestors(selectedKey, parentMap);
      return Array.from(new Set<React.Key>([...selectedKeyAncestors, ...expandedByMode]));
    }

    return expandedByMode;
  }, [isPopoverOpen, searchMatchKeys, selectedKey, parentMap, expansionMode]);

  useEffect(() => {
    if (!isPopoverOpen) {
      setManuallyExpandedKeys([]);
    }
  }, [isPopoverOpen]);

  const handleExpand: TreeProps['onExpand'] = useCallback((keys: React.Key[]) => {
    setManuallyExpandedKeys(keys);
  }, []);

  const expandedKeys = manuallyExpandedKeys.length > 0 ? manuallyExpandedKeys : calculatedExpandedKeys;

  return {
    expandedKeys,
    handleExpand
  };
};
